import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { AdminPageShell, AdminPanelSection } from '../admin/AdminPageShell'
import type { AdminSession } from '../admin/AdminShell'
import { catalogLabel } from '../../lib/catalog-label'
import { resolveApiBaseUrl } from '../../lib/api-base-url'
import { PasswordField } from '../../components/PasswordField'

type ProfileForm = {
  name: string
  email: string
  currentPassword: string
  password: string
  passwordConfirmation: string
}

type ProfilePageProps = {
  session: AdminSession
  onSessionUpdate: (session: AdminSession) => void
}

const apiBaseUrl = resolveApiBaseUrl()

export function ProfilePage({ session, onSessionUpdate }: ProfilePageProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState<ProfileForm>({
    name: session.user.name,
    email: session.user.email,
    currentPassword: '',
    password: '',
    passwordConfirmation: '',
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; message: string } | null>(null)

  useEffect(() => {
    setForm({
      name: session.user.name,
      email: session.user.email,
      currentPassword: '',
      password: '',
      passwordConfirmation: '',
    })
  }, [session.user.email, session.user.name])

  useEffect(() => {
    let active = true

    const loadProfile = async () => {
      const response = await fetch(`${apiBaseUrl}/profile`, {
        headers: {
          Authorization: `Bearer ${session.session.token}`,
        },
      })
      const payload = (await response.json().catch(() => null)) as AdminSession | null

      if (active && response.ok && payload) {
        onSessionUpdate(payload)
      }
    }

    void loadProfile()

    return () => {
      active = false
    }
  }, [session.session.token])

  const saveProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSaving(true)
    setNotice(null)

    try {
      const response = await fetch(`${apiBaseUrl}/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.session.token}`,
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          currentPassword: form.currentPassword,
          password: form.password,
          passwordConfirmation: form.passwordConfirmation,
        }),
      })
      const payload = (await response.json().catch(() => null)) as AdminSession | { message?: string } | null

      if (!response.ok) {
        throw new Error((payload as { message?: string } | null)?.message || t('admin.profile.error'))
      }

      const nextSession = payload as AdminSession
      onSessionUpdate(nextSession)
      setForm({
        name: nextSession.user.name,
        email: nextSession.user.email,
        currentPassword: '',
        password: '',
        passwordConfirmation: '',
      })
      setIsEditing(false)
      setNotice({ tone: 'success', message: t('admin.profile.saved') })
    } catch (error) {
      setNotice({
        tone: 'error',
        message: error instanceof Error ? error.message : t('admin.profile.error'),
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <AdminPageShell title={t('admin.nav.profile')} icon={<ProfileIcon />}>
      <div className="admin-profile">
        {notice ? (
          <div className={`admin-inline-notice admin-inline-notice--${notice.tone}`}>
            <strong>{notice.message}</strong>
          </div>
        ) : null}

        <AdminPanelSection title={t('admin.profile.access')}>
          <dl className="admin-profile__list">
            <div>
              <dt>{t('admin.profile.field.name')}</dt>
              <dd>{session.user.name}</dd>
            </div>
            <div>
              <dt>{t('admin.profile.field.email')}</dt>
              <dd>{session.user.email}</dd>
            </div>
            <div>
              <dt>{t('admin.profile.field.role')}</dt>
              <dd>{catalogLabel(session.user.role, t)}</dd>
            </div>
            <div>
              <dt>{t('admin.profile.field.status')}</dt>
              <dd>{session.user.status}</dd>
            </div>
            <div>
              <dt>{t('admin.profile.field.organization')}</dt>
              <dd>{session.organization.name}</dd>
            </div>
            <div>
              <dt>{t('admin.profile.field.expires')}</dt>
              <dd>{formatDateTime(session.session.expiresAt)}</dd>
            </div>
          </dl>
        </AdminPanelSection>

        <AdminPanelSection title={t('admin.profile.permissions')}>
          <ul className="admin-permission-list">
            {session.user.permissions.map((permission) => (
              <li key={permission}>
                <strong>{catalogLabel(permission, t)}</strong>
              </li>
            ))}
          </ul>
        </AdminPanelSection>

        <AdminPanelSection title={t('admin.profile.edit')}>
          {isEditing ? (
            <form className="admin-form admin-profile-form" onSubmit={saveProfile}>
              <label>
                <span>{t('admin.profile.field.name')}</span>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                />
              </label>

              <label>
                <span>{t('admin.profile.field.email')}</span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                />
              </label>

              <div className="admin-profile-form__passwords">
                <PasswordField
                  label={t('admin.profile.field.currentPassword')}
                  value={form.currentPassword}
                  onChange={(value) => setForm((current) => ({ ...current, currentPassword: value }))}
                  autoComplete="current-password"
                  variant="admin"
                  required
                />
                <PasswordField
                  label={t('admin.profile.field.password')}
                  value={form.password}
                  onChange={(value) => setForm((current) => ({ ...current, password: value }))}
                  autoComplete="new-password"
                  variant="admin"
                  required
                />
                <PasswordField
                  label={t('admin.profile.field.passwordConfirmation')}
                  value={form.passwordConfirmation}
                  onChange={(value) => setForm((current) => ({ ...current, passwordConfirmation: value }))}
                  autoComplete="new-password"
                  variant="admin"
                  required
                />
              </div>

              <div className="admin-profile-form__actions">
                <button
                  type="button"
                  className="admin-link-button"
                  onClick={() => {
                    setForm({
                      name: session.user.name,
                      email: session.user.email,
                      currentPassword: '',
                      password: '',
                      passwordConfirmation: '',
                    })
                    setIsEditing(false)
                  }}
                >
                  <XIcon />
                  {t('common.cancel')}
                </button>
                <button type="submit" className="admin-action-button" disabled={isSaving}>
                  <CheckIcon />
                  {isSaving ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              className="admin-action-button"
              onClick={() => {
                setForm({
                  name: session.user.name,
                  email: session.user.email,
                  currentPassword: '',
                  password: '',
                  passwordConfirmation: '',
                })
                setIsEditing(true)
              }}
            >
              <EditIcon />
              {t('admin.profile.edit')}
            </button>
          )}
        </AdminPanelSection>
      </div>
    </AdminPageShell>
  )
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

function ProfileIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="8" r="4" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m5 12 4 4 10-10" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </svg>
  )
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m4 20 4.5-1 10-10-3.5-3.5-10 10Z" />
      <path d="m13 6 3.5 3.5" />
    </svg>
  )
}
